import { useState } from "react";
import { RiArrowRightLine, RiCheckLine } from "react-icons/ri";
import { type GasSurveyRecord } from "@screen/Survey/surveyStorage";
import { type BehaviorAlert } from "./intervalGasView.model";

// ─── Colors ───────────────────────────────────────────────────────────────────

const C = {
  ink:        "#1f2937",
  muted:      "#8b909c",
  border:     "#EAECF0",
  cardBg:     "#fff",
  blue:       "#2196f3",
  green:      "#16a34a",
  greenTint:  "#E8FDF5",
  amber:      "#d97706",
  amberTint:  "#FFFBEB",
  red:        "#dc2626",
  redTint:    "#FEF2F2",
  indigo:     "#6366F1",
  indigoTint: "#EEF2FF",
};

// ─── Data ─────────────────────────────────────────────────────────────────────

type AccuracyKey = "perfect" | "early" | "late";
type SituationKey = "abroad" | "heavy" | "skip";

const ACCURACY_OPTS: { key: AccuracyKey; label: string; sub: string; color: string; tint: string }[] = [
  { key: "perfect", label: "很準，剛好快用完",   sub: "這次提醒時機剛剛好",  color: C.green,  tint: C.greenTint  },
  { key: "early",   label: "提醒偏早，氣還很多", sub: "希望晚幾天再通知我",  color: C.amber,  tint: C.amberTint  },
  { key: "late",    label: "提醒偏晚，快沒氣了", sub: "希望提早幾天通知我",  color: C.red,    tint: C.redTint    },
];

const GRID_OPTS: Record<"abroad" | "heavy", { label: string; desc: string; color: string; tint: string }> = {
  abroad: { label: "準備出國",  desc: "暫停幾天的提醒",       color: C.indigo, tint: C.indigoTint },
  heavy:  { label: "用量較多",  desc: "這段時間用比平常多",   color: C.amber,  tint: C.amberTint  },
};

const SUB_OPTS: Record<SituationKey, { key: string; label: string }[]> = {
  abroad: [
    { key: "1w",  label: "出國約 1 週"  },
    { key: "2w",  label: "出國約 2 週"  },
    { key: "1m",  label: "出國 1 個月以上" },
    { key: "biz", label: "短期出差"     },
  ],
  heavy: [
    { key: "winter",  label: "冬季取暖"   },
    { key: "guests",  label: "來客較多"   },
    { key: "cooking", label: "料理增多"   },
    { key: "family",  label: "家人增加"   },
    { key: "other",   label: "其他原因"   },
  ],
  skip: [
    { key: "done",    label: "已自行叫桶" },
    { key: "plenty",  label: "氣還很多"   },
    { key: "later",   label: "暫時不需要" },
    { key: "other",   label: "其他"       },
  ],
};

const USAGE_LABELS: Record<string, string> = {
  cooking:   "瓦斯爐",
  hot_water: "熱水器",
  heating:   "暖爐",
  dryer:     "烘衣機",
  tea:       "泡茶",
};

const HOUSEHOLD_LABELS: Record<string, string> = {
  "1":   "1 人",
  "2":   "2 人",
  "3-4": "3–4 人",
  "5+":  "5 人以上",
};

// ─── Behavior Alert Card ──────────────────────────────────────────────────────

const ALERT_CONFIG: Record<BehaviorAlert["type"], {
  title: string;
  desc: string;
  reasons: string[];
}> = {
  early: {
    title: "偵測到用氣量增加",
    desc: "最近您的叫桶頻率比過去高許多，是否有特殊狀況？",
    reasons: ["家庭人口增加", "季節性用量增加", "更換熱水器", "增加瓦斯爐具", "其他原因"],
  },
  late: {
    title: "偵測到用氣量減少",
    desc: "最近您的叫桶間隔比過去長很多，是否有什麼變化？",
    reasons: ["出國或長假", "家庭人口減少", "改用其他能源", "長期外宿", "其他原因"],
  },
  irregular: {
    title: "偵測到用氣模式不規律",
    desc: "您最近叫桶的時間間隔比以往不固定，生活型態是否有改變？",
    reasons: ["生活型態改變", "節慶假日影響", "出差頻繁", "家中裝修", "其他原因"],
  },
};

const alertAckKey = (contextId: string | number | null | undefined, type: string) =>
  `behavior_alert_ack_v1:${contextId ?? "default"}:${type}`;

const loadAlertAck = (contextId: string | number | null | undefined, type: string): string | null => {
  try {
    const raw = localStorage.getItem(alertAckKey(contextId, type));
    if (!raw) return null;
    const p = JSON.parse(raw) as { date: string; reason: string | null };
    return p.date === todayStr() ? (p.reason ?? "") : null;
  } catch { return null; }
};

const saveAlertAck = (contextId: string | number | null | undefined, type: string, reason: string | null) => {
  try {
    localStorage.setItem(alertAckKey(contextId, type), JSON.stringify({ date: todayStr(), reason }));
  } catch { /* silent */ }
};

/** 每個 alert type 的視覺主題：白卡＋頂部色條，避免整卡糊色底 */
const ALERT_THEME: Record<BehaviorAlert["type"], {
  cardBg:    string;
  accent:    string;
  badgeBg:   string;
  badgeColor: string;
  badgeLabel: string;
  bigTitle:  string;
  chipColor: string;
  chipShadow: string;
}> = {
  early: {
    cardBg: "#FFFFFF",
    accent: "#F59E0B",
    badgeBg: "#FFFBEB",
    badgeColor: "#B45309",
    badgeLabel: "用量增加偵測",
    bigTitle: "最近用氣量\n增加了",
    chipColor: "#D97706",
    chipShadow: "rgba(217,119,6,0.35)",
  },
  late: {
    cardBg: "#FFFFFF",
    accent: "#0EA5E9",
    badgeBg: "#F0F9FF",
    badgeColor: "#0369A1",
    badgeLabel: "用量減少偵測",
    bigTitle: "最近用氣量\n減少了",
    chipColor: "#0284C7",
    chipShadow: "rgba(2,132,199,0.32)",
  },
  irregular: {
    cardBg: "#FFFFFF",
    accent: "#14B8A6",
    badgeBg: "#F0FDFA",
    badgeColor: "#0F766E",
    badgeLabel: "模式變化偵測",
    bigTitle: "用氣模式\n有些不一樣",
    chipColor: "#0D9488",
    chipShadow: "rgba(13,148,136,0.32)",
  },
};

const AlertReasonChip = ({
  label, active, onClick, chipColor, chipShadow,
}: { label: string; active: boolean; onClick: () => void; chipColor: string; chipShadow: string }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: "9px 15px", borderRadius: 999,
      fontSize: 13, fontWeight: active ? 700 : 500, fontFamily: "inherit",
      color: active ? "#fff" : C.ink,
      background: active ? chipColor : "#F5F7FA",
      border: active ? `1.5px solid ${chipColor}` : "1.5px solid #E8ECF0",
      boxShadow: active ? `0 4px 14px -4px ${chipShadow}` : "none",
      cursor: "pointer", transition: "all 0.18s ease", whiteSpace: "nowrap",
    }}>
    {label}
  </button>
);

const BehaviorAlertCard = ({
  alert, contextId,
}: { alert: BehaviorAlert; contextId?: string | number | null }) => {
  const cfg   = ALERT_CONFIG[alert.type];
  const theme = ALERT_THEME[alert.type];
  const acked = loadAlertAck(contextId, alert.type);
  const [mode, setMode]       = useState<"form" | "done">(acked !== null ? "done" : "form");
  const [selected, setSelected] = useState<string | null>(acked || null);

  const todayDisplay = (() => { const d = new Date(); return `${d.getMonth() + 1}/${d.getDate()}`; })();

  const handleSubmit = (reason: string | null) => {
    saveAlertAck(contextId, alert.type, reason);
    setSelected(reason);
    setMode("done");
  };

  /* ── Done state ── */
  if (mode === "done") return (
    <div style={{ background: "#fff", borderRadius: 18, padding: "18px 16px", boxShadow: "0 3px 10px -8px rgba(31,41,55,0.18)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>{theme.bigTitle.replace("\n", "")}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>今日已確認</div>
        </div>
        <button type="button" onClick={() => setMode("form")} style={{ fontSize: 12, fontWeight: 600, color: theme.badgeColor, background: theme.badgeBg, border: "none", padding: "5px 12px", borderRadius: 999, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
          修改
        </button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", background: theme.badgeBg, borderRadius: 12 }}>
        <div style={{ width: 7, height: 7, borderRadius: "50%", background: theme.badgeColor, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>{selected || "無特殊原因"}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{todayDisplay} 已確認</div>
        </div>
      </div>
    </div>
  );

  /* ── Form state：白卡＋頂部色條 ── */
  return (
    <div
      style={{
        background: theme.cardBg,
        borderRadius: 18,
        overflow: "hidden",
        border: "1px solid #E8ECF0",
        boxShadow: "0 6px 18px -12px rgba(31,41,55,0.22)",
      }}>
      <div style={{ height: 4, background: theme.accent }} />
      <div style={{ padding: "18px 18px 16px" }}>
      {/* Badge */}
      <div style={{ display: "inline-flex", alignItems: "center", padding: "4px 11px", borderRadius: 999, background: theme.badgeBg, marginBottom: 14 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: theme.badgeColor, letterSpacing: 0.2 }}>
          {theme.badgeLabel}
        </span>
      </div>

      {/* Big title — 大字，帶換行 */}
      <div style={{ fontSize: 24, fontWeight: 800, color: C.ink, lineHeight: 1.25, letterSpacing: -0.5, marginBottom: 10, whiteSpace: "pre-line" }}>
        {theme.bigTitle}
      </div>

      {/* Description */}
      <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.65, marginBottom: 14 }}>
        {cfg.desc}
      </div>

      {/* Delta stat pill */}
      {alert.delta_days != null && Math.abs(alert.delta_days) > 0 && (
        <div style={{ display: "inline-flex", alignItems: "center", padding: "5px 12px", borderRadius: 999, background: theme.badgeBg, marginBottom: 16 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: theme.badgeColor }}>
            比歷史均值{alert.delta_days > 0 ? "提早" : "延遲"} {Math.abs(alert.delta_days)} 天
          </span>
        </div>
      )}

      {/* Divider + chips section */}
      <div style={{ height: 1, background: "#EEF1F5", margin: "0 0 16px" }} />
      <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 10, letterSpacing: 0.2 }}>可能的原因是？</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: selected ? 14 : 6 }}>
        {cfg.reasons.map((r) => (
          <AlertReasonChip key={r} label={r} active={selected === r} chipColor={theme.chipColor} chipShadow={theme.chipShadow} onClick={() => setSelected(selected === r ? null : r)} />
        ))}
      </div>

      {/* Submit */}
      {selected && (
        <button type="button" onClick={() => handleSubmit(selected)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: theme.chipColor, border: "none", borderRadius: 14, padding: "13px 16px", cursor: "pointer", fontFamily: "inherit", boxShadow: `0 6px 18px -8px ${theme.chipShadow}`, marginBottom: 10 }}>
          <div>
            <div style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>確認送出</div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11, marginTop: 1 }}>{selected}</div>
          </div>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RiCheckLine size={16} color={theme.chipColor} />
          </div>
        </button>
      )}

      <button type="button" onClick={() => handleSubmit(null)} style={{ width: "100%", background: "transparent", border: "none", padding: "8px 0", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, color: C.muted, textAlign: "center" }}>
        沒有，正常使用
      </button>
      </div>
    </div>
  );
};

// ─── Card 1：這次提醒準嗎？ ───────────────────────────────────────────────────

const AccuracyRow = ({
  opt, active, submitted, onClick,
}: {
  opt: typeof ACCURACY_OPTS[0];
  active: boolean;
  submitted: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={submitted}
    style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "14px 16px 14px 0",
      background: active ? opt.tint : "transparent",
      border: "none", width: "100%", textAlign: "left",
      cursor: submitted ? "default" : "pointer",
      fontFamily: "inherit", transition: "background 0.15s ease",
    }}>
    <div style={{
      width: 4, alignSelf: "stretch", minHeight: 40, borderRadius: 2, flexShrink: 0,
      background: active ? opt.color : C.border,
      transition: "background 0.15s ease",
    }} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: active ? 700 : 600, color: active ? opt.color : C.ink }}>
        {opt.label}
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{opt.sub}</div>
    </div>
    {active
      ? <RiCheckLine size={18} color={opt.color} style={{ flexShrink: 0, marginRight: 2 }} />
      : !submitted && <RiArrowRightLine size={16} color={C.muted} style={{ flexShrink: 0, marginRight: 2 }} />
    }
  </button>
);

const FeedbackCard = () => {
  const [accuracy, setAccuracy] = useState<AccuracyKey | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const accuracyOpt = ACCURACY_OPTS.find((o) => o.key === accuracy);

  return (
    <div style={{ background: C.cardBg, borderRadius: 18, overflow: "hidden", boxShadow: "0 3px 10px -8px rgba(31,41,55,0.18)" }}>
      {submitted ? (
        <div style={{ padding: "20px 18px", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 14, flexShrink: 0,
            background: accuracyOpt?.tint ?? C.greenTint,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <RiCheckLine size={22} color={accuracyOpt?.color ?? C.green} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>已記錄，謝謝！</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
              {accuracyOpt?.label}・下次叫桶後可再回饋
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* 灰色 header zone，與 row 區明確分層 */}
          <div style={{ background: "#F7F8FB", padding: "18px 18px 14px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.ink }}>這次提醒準嗎？</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              回饋幫助 AI 持續優化提醒時機
            </div>
          </div>
          <div style={{ marginTop: 0 }}>
            {ACCURACY_OPTS.map((opt, i) => (
              <div key={opt.key}>
                {i > 0 && <div style={{ height: 1, background: C.border, margin: "0 16px" }} />}
                <AccuracyRow
                  opt={opt}
                  active={accuracy === opt.key}
                  submitted={submitted}
                  onClick={() => setAccuracy(accuracy === opt.key ? null : opt.key)}
                />
              </div>
            ))}
          </div>
          {accuracy && (
            <div style={{ padding: "10px 16px 16px" }}>
              <button
                type="button"
                onClick={() => setSubmitted(true)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: C.blue, border: "none", borderRadius: 14,
                  padding: "13px 16px", cursor: "pointer", fontFamily: "inherit",
                  boxShadow: "0 6px 18px -8px rgba(33,150,243,0.5)",
                }}>
                <div>
                  <div style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>送出回饋</div>
                  <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 11, marginTop: 1 }}>
                    幫助 AI 優化提醒時機
                  </div>
                </div>
                <div style={{
                  width: 34, height: 34, borderRadius: "50%", background: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <RiArrowRightLine size={16} color={C.blue} />
                </div>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Situation report storage ─────────────────────────────────────────────────

interface SituationReport {
  date: string;
  situation: SituationKey;
  sub: string | null;
}

const todayStr = () => new Date().toLocaleDateString("en-CA");

const situationKey = (contextId: string | number | null | undefined) =>
  `situation_report_v1:${contextId ?? "default"}`;

const loadTodayReport = (contextId: string | number | null | undefined): SituationReport | null => {
  try {
    const raw = localStorage.getItem(situationKey(contextId));
    if (!raw) return null;
    const p = JSON.parse(raw) as SituationReport;
    return p.date === todayStr() ? p : null;
  } catch { return null; }
};

const saveTodayReport = (contextId: string | number | null | undefined, r: SituationReport) => {
  try { localStorage.setItem(situationKey(contextId), JSON.stringify(r)); } catch { /* silent */ }
};

// ─── Card 2：特殊情況回報 ─────────────────────────────────────────────────────

const SubChip = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: "6px 13px", borderRadius: 999,
      fontSize: 12.5, fontWeight: active ? 700 : 500,
      fontFamily: "inherit",
      color: active ? "#fff" : C.ink,
      background: active ? C.blue : "#ECEEF2",
      border: active ? `1.5px solid ${C.blue}` : "1.5px solid transparent",
      boxShadow: active ? "0 3px 10px -4px rgba(33,150,243,0.4)" : "none",
      cursor: "pointer", transition: "all 0.15s ease", whiteSpace: "nowrap",
    }}>
    {label}
  </button>
);

const SituationGrid = ({ contextId }: { contextId?: string | number | null }) => {
  const stored = loadTodayReport(contextId);
  const [mode, setMode] = useState<"form" | "done">(stored ? "done" : "form");
  const [active, setActive] = useState<SituationKey | null>(stored?.situation ?? null);
  const [sub, setSub] = useState<string | null>(stored?.sub ?? null);

  const toggle = (key: SituationKey) => {
    setActive((v) => (v === key ? null : key));
    setSub(null);
  };

  const handleSubmit = () => {
    if (!active) return;
    saveTodayReport(contextId, { date: todayStr(), situation: active, sub });
    setMode("done");
  };

  const skipOn = active === "skip";

  const situationLabel =
    active === "abroad" ? "準備出國" :
    active === "heavy"  ? "用量較多" :
    active === "skip"   ? "此次忽略" : "";
  const subLabel = active ? (SUB_OPTS[active].find((s) => s.key === sub)?.label ?? null) : null;
  const summaryText = [situationLabel, subLabel].filter(Boolean).join("・");

  const today = new Date();
  const todayDisplay = `${today.getMonth() + 1}/${today.getDate()}`;

  const CELL_IDLE: Record<"abroad" | "heavy", string> = {
    abroad: "rgba(99,102,241,0.07)",
    heavy:  "rgba(217,119,6,0.07)",
  };

  /* ── Done state ── */
  if (mode === "done") {
    return (
      <div style={{ background: "#F4F5F8", borderRadius: 18, padding: "18px 16px", boxShadow: "none" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.ink }}>特殊情況回報</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>今日已回報</div>
          </div>
          <button
            type="button"
            onClick={() => setMode("form")}
            style={{
              fontSize: 12, fontWeight: 600, color: C.blue,
              background: "#EBF5FF", border: "none",
              padding: "5px 12px", borderRadius: 999,
              cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
            }}>
            修改
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "rgba(255,255,255,0.7)", borderRadius: 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{summaryText || "已回報"}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{todayDisplay} 已送出</div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Form state ── */
  return (
    <div style={{ background: "#F4F5F8", borderRadius: 18, padding: "18px 16px", boxShadow: "none" }}>
      <div style={{ fontSize: 17, fontWeight: 800, color: C.ink, marginBottom: 4 }}>特殊情況回報</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
        告訴我們你的情況，讓預測更貼近你
      </div>

      {/* 2-col grid — 預設就帶淡色調，選中加深 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {(["abroad", "heavy"] as const).map((key) => {
          const on = active === key;
          const meta = GRID_OPTS[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              style={{
                padding: "15px 13px", borderRadius: 14, textAlign: "left",
                background: on ? meta.tint : CELL_IDLE[key],
                border: `1.5px solid ${on ? meta.color : "transparent"}`,
                cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s ease",
              }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: on ? meta.color : C.ink }}>{meta.label}</span>
                {on && <RiCheckLine size={15} color={meta.color} />}
              </div>
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.45 }}>{meta.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Sub-chips for abroad / heavy */}
      {(active === "abroad" || active === "heavy") && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 8, letterSpacing: 0.2 }}>說明情況</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {SUB_OPTS[active].map((s) => (
              <SubChip key={s.key} label={s.label} active={sub === s.key} onClick={() => setSub(sub === s.key ? null : s.key)} />
            ))}
          </div>
        </div>
      )}

      {/* Ignore — full width */}
      <button
        type="button"
        onClick={() => toggle("skip")}
        style={{
          marginTop: 10, width: "100%",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "13px 14px", borderRadius: 14,
          background: skipOn ? "rgba(196,203,214,0.25)" : "rgba(0,0,0,0.04)",
          border: `1.5px solid ${skipOn ? "#C4CBD6" : "transparent"}`,
          cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s ease",
        }}>
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>此次忽略，選擇原因</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>我知道了，這次不需要叫桶</div>
        </div>
        {skipOn
          ? <RiCheckLine size={17} color={C.muted} style={{ flexShrink: 0 }} />
          : <RiArrowRightLine size={15} color={C.muted} style={{ flexShrink: 0 }} />
        }
      </button>

      {/* Sub-chips for skip */}
      {skipOn && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 8, letterSpacing: 0.2 }}>原因是？</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {SUB_OPTS.skip.map((s) => (
              <SubChip key={s.key} label={s.label} active={sub === s.key} onClick={() => setSub(sub === s.key ? null : s.key)} />
            ))}
          </div>
        </div>
      )}

      {/* 確認送出 */}
      {active && (
        <button
          type="button"
          onClick={handleSubmit}
          style={{
            marginTop: 14, width: "100%",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: C.blue, border: "none", borderRadius: 14,
            padding: "13px 16px", cursor: "pointer", fontFamily: "inherit",
            boxShadow: "0 6px 18px -8px rgba(33,150,243,0.5)",
          }}>
          <div>
            <div style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>確認送出</div>
            <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 11, marginTop: 1 }}>
              {summaryText || situationLabel}
            </div>
          </div>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RiCheckLine size={16} color={C.blue} />
          </div>
        </button>
      )}
    </div>
  );
};

// ─── Card 3：你的用氣習慣（精簡摘要） ────────────────────────────────────────

const SurveySnapshotCard = ({
  record, onSurvey,
}: { record: GasSurveyRecord; onSurvey: () => void }) => {
  const usageText = record.usage_types?.length
    ? record.usage_types.map((v) => USAGE_LABELS[v] ?? v).join("、")
    : null;

  const rows: { label: string; value: string }[] = [
    { label: "自填使用週期", value: `${record.survey_days} 天` },
    { label: "鋼瓶規格",     value: record.cylinder_kg ? `${record.cylinder_kg} kg` : "不確定" },
    ...(record.household_size
      ? [{ label: "家庭人數", value: HOUSEHOLD_LABELS[record.household_size] ?? record.household_size }]
      : []),
    ...(usageText ? [{ label: "主要用途", value: usageText }] : []),
  ];

  return (
    /* 暖米色底，與其他白卡明確不同 */
    <div style={{ background: "#FDFCF9", borderRadius: 18, padding: "16px 16px 18px", boxShadow: "0 2px 8px -5px rgba(31,41,55,0.10)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>你的用氣習慣</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>問卷自填資料</div>
        </div>
        <button
          type="button"
          onClick={onSurvey}
          style={{
            fontSize: 11.5, fontWeight: 600, color: "#92714A",
            background: "#F5EFE6", border: "none",
            padding: "5px 12px", borderRadius: 999,
            cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
          }}>
          更新問卷
        </button>
      </div>

      {/* 2×2 stat grid — 白色小格浮在米色底上 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
        {rows.map((row, i) => (
          <div
            key={i}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: "12px 13px",
              boxShadow: "0 1px 4px -2px rgba(31,41,55,0.08)",
            }}>
            <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 600, letterSpacing: 0.3, marginBottom: 6, whiteSpace: "nowrap" }}>
              {row.label}
            </div>
            <div style={{
              fontSize: row.value.length > 6 ? 13 : 17,
              fontWeight: 800, color: C.ink, lineHeight: 1.2,
            }}>
              {row.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Style variants ───────────────────────────────────────────────────────────

export type StyleV = "A" | "B" | "C";

/** A = 結構分層（current）  B = 大字簡約  C = 色塊分類 */
export const STYLE_VARIANTS: { key: StyleV; label: string; desc: string }[] = [
  { key: "A", label: "結構分層", desc: "灰底 + 標題區 + 米色摘要" },
  { key: "B", label: "大字簡約", desc: "純白 + 更大字型 + 橫排摘要" },
  { key: "C", label: "色塊分類", desc: "各 section 帶色底 + 白浮格" },
];

// ─── Variant-driven style tokens ──────────────────────────────────────────────

interface CardTokens {
  cardBg: string;
  cardShadow: string;
  titleColor: string;
  titleSize: number;
  subtitleColor: string;
  /** FeedbackCard: 標題區背景（undefined = 無獨立標題區）*/
  headerZoneBg?: string;
  /** SituationGrid: idle cell 背景 */
  cellIdleBg: (key: "abroad" | "heavy") => string;
  cellIdleBorder: string;
  /** SurveySnapshot: 底色 & 磚塊色 */
  snapBg: string;
  snapTileBg: string;
  snapTitleColor: string;
  snapUpdateColor: string;
  snapUpdateBg: string;
}

const TOKENS: Record<StyleV, CardTokens> = {
  A: {
    cardBg: "#fff",
    cardShadow: "0 3px 10px -8px rgba(31,41,55,0.18)",
    titleColor: C.ink,
    titleSize: 17,
    subtitleColor: C.muted,
    headerZoneBg: "#F7F8FB",
    cellIdleBg: (k) => k === "abroad" ? "rgba(99,102,241,0.07)" : "rgba(217,119,6,0.07)",
    cellIdleBorder: "transparent",
    snapBg: "#FDFCF9",
    snapTileBg: "#fff",
    snapTitleColor: C.ink,
    snapUpdateColor: "#92714A",
    snapUpdateBg: "#F5EFE6",
  },
  B: {
    cardBg: "#fff",
    cardShadow: "0 4px 14px -8px rgba(31,41,55,0.14)",
    titleColor: C.ink,
    titleSize: 20,
    subtitleColor: C.muted,
    headerZoneBg: undefined,
    cellIdleBg: () => "#F5F5F7",
    cellIdleBorder: "#E5E7EB",
    snapBg: "#fff",
    snapTileBg: "#F7F8FA",
    snapTitleColor: C.ink,
    snapUpdateColor: C.blue,
    snapUpdateBg: "#EBF5FF",
  },
  C: {
    cardBg: "#EFF6FF",
    cardShadow: "none",
    titleColor: "#1E40AF",
    titleSize: 17,
    subtitleColor: "#4B72B0",
    headerZoneBg: undefined,
    cellIdleBg: () => "#fff",
    cellIdleBorder: "#E5E7EB",
    snapBg: "#FFFBEB",
    snapTileBg: "#fff",
    snapTitleColor: "#92400E",
    snapUpdateColor: "#92400E",
    snapUpdateBg: "#FEF3C7",
  },
};

// ─── Survey Dashboard（頂部資訊區，無卡片框）────────────────────────────────────

const SurveyDashboard = ({
  record, v,
  lastOrderDate, avgDays, notifyDate,
  onUpdateSurvey,
}: {
  record: GasSurveyRecord;
  v: StyleV;
  lastOrderDate?: string | null;
  avgDays?: number | null;
  notifyDate?: string | null;
  onUpdateSurvey?: () => void;
}) => {
  const t = TOKENS[v];

  // 距上次叫桶幾天（動態）
  const daysSince = lastOrderDate
    ? Math.max(0, Math.floor((Date.now() - new Date(`${lastOrderDate}T00:00:00+08:00`).getTime()) / 86_400_000))
    : null;

  // 下次提醒日 → 格式化為 M/D
  const notifyStr = notifyDate
    ? `${new Date(`${notifyDate}T00:00:00+08:00`).getMonth() + 1}/${new Date(`${notifyDate}T00:00:00+08:00`).getDate()}`
    : null;

  // 最多 4 個 tile：動態優先，再補問卷
  const stats: { label: string; value: string; dimmed?: boolean }[] = [
    ...(daysSince !== null
      ? [{ label: "已過天數", value: `${daysSince} 天` }]
      : [{ label: "自填週期", value: `${record.survey_days} 天`, dimmed: true }]),
    ...(avgDays != null ? [{ label: "歷史均值", value: `${avgDays} 天` }] : []),
    ...(notifyStr ? [{ label: "下次提醒", value: notifyStr }] : []),
    { label: "鋼瓶規格", value: record.cylinder_kg ? `${record.cylinder_kg} kg` : "不確定" },
    ...(record.household_size ? [{ label: "家庭人數", value: HOUSEHOLD_LABELS[record.household_size] ?? record.household_size }] : []),
  ].slice(0, 4);  // 最多 4 格，避免單行太窄

  const tileBg  = v === "C" ? "#FFFBEB" : v === "B" ? "#F7F8FA" : "#fff";
  const tileVal = v === "C" ? "#92400E" : C.ink;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, margin: "0 0 10px 4px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: t.titleColor }}>你的用氣習慣</span>
        </div>
        {onUpdateSurvey && (
          <button
            type="button"
            onClick={onUpdateSurvey}
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#92714A",
              background: "#F5EFE6",
              padding: "4px 10px",
              borderRadius: 999,
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
            }}>
            更新
          </button>
        )}
      </div>

      {/* Stat tiles：平均分配 100% 寬度 */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 10 }}>
        {stats.map((s, i) => (
          <div
            key={i}
            style={{
              background: tileBg,
              borderRadius: 13,
              padding: "10px 12px",
              boxShadow: "0 2px 8px -4px rgba(31,41,55,0.10)",
              minWidth: 0,
            }}>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, letterSpacing: 0.3, marginBottom: 5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {s.label}
            </div>
            <div style={{ fontSize: s.value.length > 6 ? 12 : 16, fontWeight: 800, color: s.dimmed ? C.muted : tileVal, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── QuickActionRow：兩個 tile 展開互動 ───────────────────────────────────────

const QuickBtn = ({
  title, sub, active, done, onClick,
}: { title: string; sub: string; active: boolean; done: boolean; onClick: () => void }) => {
  const [pressing, setPressing] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={() => setPressing(true)}
      onPointerUp={() => setPressing(false)}
      onPointerLeave={() => setPressing(false)}
      onPointerCancel={() => setPressing(false)}
      style={{
        flex: 1,
        padding: "14px 14px",
        borderRadius: 14,
        textAlign: "left",
        background: done ? C.greenTint : active ? "#EBF5FF" : "#fff",
        border: `1.5px solid ${done ? C.green : active ? C.blue : C.border}`,
        cursor: "pointer",
        fontFamily: "inherit",
        /* press → scale 0.965，放開 → spring 彈回 */
        transform: pressing ? "scale(0.965)" : "scale(1)",
        transition: pressing
          ? "transform 0.08s ease, background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease"
          : "transform 0.45s cubic-bezier(0.34,1.56,0.64,1), background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: pressing
          ? "0 1px 4px -2px rgba(31,41,55,0.12)"
          : active
          ? "0 4px 16px -6px rgba(33,150,243,0.38)"
          : "0 2px 10px -4px rgba(31,41,55,0.12)",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: done ? C.green : active ? C.blue : C.ink, lineHeight: 1.3, transition: "color 0.2s ease" }}>
          {title}
        </span>
        {/* 永遠顯示箭頭，讓使用者知道可以收合；done 時加小 check dot */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginTop: 1 }}>
          {done && <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.green }} />}
          <span style={{
            fontSize: 15,
            color: active ? (done ? C.green : C.blue) : C.muted,
            display: "inline-block",
            transform: active ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.45s cubic-bezier(0.34,1.56,0.64,1), color 0.2s ease",
          }}>▾</span>
        </div>
      </div>
      <div style={{ fontSize: 11, color: done ? C.green : C.muted, marginTop: 5, transition: "color 0.2s ease" }}>
        {done ? "今日已完成 ✓" : sub}
      </div>
    </button>
  );
};

const QuickActionRow = ({ contextId, v }: { contextId?: string | number | null; v: StyleV }) => {
  const t = TOKENS[v];
  const [panel, setPanel] = useState<"feedback" | "situation" | null>("feedback");
  const toggle = (p: "feedback" | "situation") => setPanel((cur) => (cur === p ? null : p));

  // ── Feedback state ──
  const [accuracy, setAccuracy] = useState<AccuracyKey | null>(null);
  const [fbDone, setFbDone] = useState(false);
  const accuracyOpt = ACCURACY_OPTS.find((o) => o.key === accuracy);

  // ── Situation state ──
  const stored = loadTodayReport(contextId);
  const [situMode, setSituMode] = useState<"form" | "done">(stored ? "done" : "form");
  const [sitActive, setSitActive] = useState<SituationKey | null>(stored?.situation ?? null);
  const [sitSub, setSitSub] = useState<string | null>(stored?.sub ?? null);

  const situLabel = sitActive === "abroad" ? "準備出國" : sitActive === "heavy" ? "用量較多" : sitActive === "skip" ? "此次忽略" : "";
  const sitSubLabel = sitActive ? (SUB_OPTS[sitActive].find((s) => s.key === sitSub)?.label ?? null) : null;
  const situSummary = [situLabel, sitSubLabel].filter(Boolean).join("・");
  const today = new Date();
  const todayDisplay = `${today.getMonth() + 1}/${today.getDate()}`;

  const handleSituSubmit = () => {
    if (!sitActive) return;
    saveTodayReport(contextId, { date: todayStr(), situation: sitActive, sub: sitSub });
    setSituMode("done");
  };

  const isOpen = panel !== null;
  const panelBg = v === "C" ? panel === "feedback" ? "#EFF6FF" : "#F0FDF4" : "#fff";

  return (
    <>
      <style>{`
        @keyframes qPanelIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes qItemIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Two trigger tiles */}
        <div style={{ display: "flex", gap: 10 }}>
          <QuickBtn
            title="這次提醒準嗎？"
            sub="點此快速回饋"
            active={panel === "feedback"}
            done={fbDone}
            onClick={() => toggle("feedback")}
          />
          <QuickBtn
            title="特殊情況回報"
            sub="告知特殊狀況"
            active={panel === "situation"}
            done={situMode === "done"}
            onClick={() => toggle("situation")}
          />
        </div>

        {/* overflow clip container — max-height 控制展開/收合 */}
        <div style={{
          overflow: "hidden",
          maxHeight: isOpen ? "760px" : "0px",
          transition: "max-height 0.42s cubic-bezier(0.4,0,0.2,1)",
        }}>
          {/* inner slide + fade layer */}
          <div style={{
            opacity: isOpen ? 1 : 0,
            transform: isOpen ? "translateY(0)" : "translateY(-10px)",
            transition: isOpen
              ? "opacity 0.28s ease 0.06s, transform 0.36s cubic-bezier(0.34,1,0.64,1) 0.06s"
              : "opacity 0.18s ease, transform 0.18s ease",
          }}>
            {/* key={panel} 切 tab 時強制重新跑動畫 */}
            <div key={panel} style={{ background: panelBg, borderRadius: 16, padding: "16px 16px 18px", boxShadow: t.cardShadow, animation: "qPanelIn 0.24s ease both" }}>

              {/* ── Feedback panel ── */}
              {panel === "feedback" && (
                fbDone ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 14, animation: "qItemIn 0.22s ease both" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 13, flexShrink: 0, background: accuracyOpt?.tint ?? C.greenTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <RiCheckLine size={20} color={accuracyOpt?.color ?? C.green} />
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>已記錄，謝謝！</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{accuracyOpt?.label}・下次叫桶後可再回饋</div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, color: v === "C" ? "#1E40AF" : t.titleColor, marginBottom: 12, animation: "qItemIn 0.18s ease both" }}>
                      這次提醒時機準嗎？
                    </div>
                    {ACCURACY_OPTS.map((opt, i) => (
                      /* 逐行 stagger 進場 */
                      <div key={opt.key} style={{ animation: `qItemIn 0.2s ease ${0.06 + i * 0.06}s both` }}>
                        {i > 0 && <div style={{ height: 1, background: C.border, margin: "0 0 0 4px" }} />}
                        <button type="button" onClick={() => setAccuracy(accuracy === opt.key ? null : opt.key)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 0 13px 0", background: accuracy === opt.key ? opt.tint : "transparent", border: "none", width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s ease", borderRadius: 8 }}>
                          <div style={{ width: 4, alignSelf: "stretch", minHeight: 36, borderRadius: 2, flexShrink: 0, background: accuracy === opt.key ? opt.color : C.border, transition: "background 0.15s ease" }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13.5, fontWeight: accuracy === opt.key ? 700 : 600, color: accuracy === opt.key ? opt.color : C.ink }}>{opt.label}</div>
                            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{opt.sub}</div>
                          </div>
                          {accuracy === opt.key ? <RiCheckLine size={16} color={opt.color} style={{ flexShrink: 0 }} /> : <RiArrowRightLine size={14} color={C.muted} style={{ flexShrink: 0 }} />}
                        </button>
                      </div>
                    ))}
                {accuracy && (
                  <button type="button" onClick={() => setFbDone(true)} style={{ marginTop: 12, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: C.blue, border: "none", borderRadius: 13, padding: "12px 15px", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 6px 18px -8px rgba(33,150,243,0.5)" }}>
                    <div>
                      <div style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>送出回饋</div>
                      <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 11, marginTop: 1 }}>幫助 AI 優化提醒時機</div>
                    </div>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <RiArrowRightLine size={15} color={C.blue} />
                    </div>
                  </button>
                )}
              </>
            )
          )}

          {/* ── Situation panel ── */}
          {panel === "situation" && (
            situMode === "done" ? (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: v === "C" ? "#14532D" : C.ink }}>特殊情況回報</div>
                  <button type="button" onClick={() => setSituMode("form")} style={{ fontSize: 11, fontWeight: 600, color: C.blue, background: "#EBF5FF", border: "none", padding: "4px 10px", borderRadius: 999, cursor: "pointer", fontFamily: "inherit" }}>修改</button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", background: "rgba(255,255,255,0.7)", borderRadius: 11 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.green, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>{situSummary || "已回報"}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{todayDisplay} 已送出</div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: v === "C" ? "#14532D" : t.titleColor, marginBottom: 12 }}>有什麼特殊情況嗎？</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                  {(["abroad", "heavy"] as const).map((key) => {
                    const on = sitActive === key;
                    const meta = GRID_OPTS[key];
                    return (
                      <button key={key} type="button" onClick={() => { setSitActive((p) => (p === key ? null : key)); setSitSub(null); }} style={{ padding: "13px 12px", borderRadius: 13, textAlign: "left", background: on ? meta.tint : TOKENS[v].cellIdleBg(key), border: `1.5px solid ${on ? meta.color : TOKENS[v].cellIdleBorder}`, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s ease" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: on ? meta.color : C.ink }}>{meta.label}</span>
                          {on && <RiCheckLine size={14} color={meta.color} />}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>{meta.desc}</div>
                      </button>
                    );
                  })}
                </div>
                {(sitActive === "abroad" || sitActive === "heavy") && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 7, letterSpacing: 0.2 }}>說明情況</div>
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                      {SUB_OPTS[sitActive].map((s) => <SubChip key={s.key} label={s.label} active={sitSub === s.key} onClick={() => setSitSub(sitSub === s.key ? null : s.key)} />)}
                    </div>
                  </div>
                )}
                <button type="button" onClick={() => { setSitActive((p) => (p === "skip" ? null : "skip")); setSitSub(null); }} style={{ marginTop: 9, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 13px", borderRadius: 13, background: sitActive === "skip" ? "rgba(196,203,214,0.2)" : TOKENS[v].cellIdleBg("abroad" as any) === "#fff" ? "#F5F5F7" : "rgba(0,0,0,0.04)", border: `1.5px solid ${sitActive === "skip" ? "#C4CBD6" : TOKENS[v].cellIdleBorder}`, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s ease" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>此次忽略，選擇原因</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>我知道了，這次不需要叫桶</div>
                  </div>
                  {sitActive === "skip" ? <RiCheckLine size={16} color={C.muted} style={{ flexShrink: 0 }} /> : <RiArrowRightLine size={14} color={C.muted} style={{ flexShrink: 0 }} />}
                </button>
                {sitActive === "skip" && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 7, letterSpacing: 0.2 }}>原因是？</div>
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                      {SUB_OPTS.skip.map((s) => <SubChip key={s.key} label={s.label} active={sitSub === s.key} onClick={() => setSitSub(sitSub === s.key ? null : s.key)} />)}
                    </div>
                  </div>
                )}
                {sitActive && (
                  <button type="button" onClick={handleSituSubmit} style={{ marginTop: 12, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: C.blue, border: "none", borderRadius: 13, padding: "12px 15px", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 6px 18px -8px rgba(33,150,243,0.5)" }}>
                    <div>
                      <div style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>確認送出</div>
                      <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 11, marginTop: 1 }}>{situSummary || situLabel}</div>
                    </div>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <RiCheckLine size={15} color={C.blue} />
                    </div>
                  </button>
                )}
              </>
            )
          )}

            </div>{/* end key={panel} card */}
          </div>{/* end inner slide+fade */}
        </div>{/* end overflow clip */}
      </div>{/* end column flex */}
    </>
  );
};

// ─── Variant-aware FeedbackCard ────────────────────────────────────────────────

const FeedbackCardV = ({ v }: { v: StyleV }) => {
  const [accuracy, setAccuracy] = useState<AccuracyKey | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const accuracyOpt = ACCURACY_OPTS.find((o) => o.key === accuracy);
  const t = TOKENS[v];

  const wrapStyle: React.CSSProperties = {
    background: t.cardBg, borderRadius: 18, overflow: "hidden", boxShadow: t.cardShadow,
  };

  if (submitted) return (
    <div style={wrapStyle}>
      <div style={{ padding: "20px 18px", display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 46, height: 46, borderRadius: 14, flexShrink: 0, background: accuracyOpt?.tint ?? C.greenTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <RiCheckLine size={22} color={accuracyOpt?.color ?? C.green} />
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: t.titleColor }}>已記錄，謝謝！</div>
          <div style={{ fontSize: 12, color: t.subtitleColor, marginTop: 3 }}>{accuracyOpt?.label}・下次叫桶後可再回饋</div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={wrapStyle}>
      {/* Header zone (A only) or plain header */}
      <div style={{ background: t.headerZoneBg ?? "transparent", padding: t.headerZoneBg ? "18px 18px 14px" : "18px 18px 0", borderBottom: t.headerZoneBg ? `1px solid ${C.border}` : "none" }}>
        <div style={{ fontSize: t.titleSize, fontWeight: 800, color: t.titleColor, letterSpacing: t.titleSize > 18 ? -0.5 : 0 }}>
          這次提醒準嗎？
        </div>
        <div style={{ fontSize: 12, color: t.subtitleColor, marginTop: 4 }}>回饋幫助 AI 持續優化提醒時機</div>
      </div>

      <div style={{ background: v === "C" ? "#fff" : undefined }}>
        {ACCURACY_OPTS.map((opt, i) => (
          <div key={opt.key}>
            {i > 0 && <div style={{ height: 1, background: C.border, margin: "0 16px" }} />}
            <AccuracyRow opt={opt} active={accuracy === opt.key} submitted={false} onClick={() => setAccuracy(accuracy === opt.key ? null : opt.key)} />
          </div>
        ))}
      </div>

      {accuracy && (
        <div style={{ padding: "10px 16px 16px", background: v === "C" ? "#fff" : undefined }}>
          <button type="button" onClick={() => setSubmitted(true)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: C.blue, border: "none", borderRadius: 14, padding: "13px 16px", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 6px 18px -8px rgba(33,150,243,0.5)" }}>
            <div>
              <div style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>送出回饋</div>
              <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 11, marginTop: 1 }}>幫助 AI 優化提醒時機</div>
            </div>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <RiArrowRightLine size={16} color={C.blue} />
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Variant-aware SituationGrid ───────────────────────────────────────────────

const SituationGridV = ({ contextId, v }: { contextId?: string | number | null; v: StyleV }) => {
  const stored = loadTodayReport(contextId);
  const [mode, setMode] = useState<"form" | "done">(stored ? "done" : "form");
  const [active, setActive] = useState<SituationKey | null>(stored?.situation ?? null);
  const [sub, setSub] = useState<string | null>(stored?.sub ?? null);
  const t = TOKENS[v];

  const toggle = (key: SituationKey) => { setActive((p) => (p === key ? null : key)); setSub(null); };
  const handleSubmit = () => { if (!active) return; saveTodayReport(contextId, { date: todayStr(), situation: active, sub }); setMode("done"); };

  const situationLabel = active === "abroad" ? "準備出國" : active === "heavy" ? "用量較多" : active === "skip" ? "此次忽略" : "";
  const subLabel = active ? (SUB_OPTS[active].find((s) => s.key === sub)?.label ?? null) : null;
  const summaryText = [situationLabel, subLabel].filter(Boolean).join("・");
  const today = new Date();
  const todayDisplay = `${today.getMonth() + 1}/${today.getDate()}`;

  const bgMap: Record<StyleV, string> = { A: "#F4F5F8", B: "#fff", C: "#F0FDF4" };
  const cardBg = bgMap[v];
  const shadow = v === "A" ? "none" : t.cardShadow;

  if (mode === "done") return (
    <div style={{ background: cardBg, borderRadius: 18, padding: "18px 16px", boxShadow: shadow }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: t.titleSize, fontWeight: 800, color: v === "C" ? "#14532D" : t.titleColor }}>特殊情況回報</div>
          <div style={{ fontSize: 12, color: t.subtitleColor, marginTop: 3 }}>今日已回報</div>
        </div>
        <button type="button" onClick={() => setMode("form")} style={{ fontSize: 12, fontWeight: 600, color: C.blue, background: "#EBF5FF", border: "none", padding: "5px 12px", borderRadius: 999, cursor: "pointer", fontFamily: "inherit" }}>修改</button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: v === "C" ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.7)", borderRadius: 12 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{summaryText || "已回報"}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{todayDisplay} 已送出</div>
        </div>
      </div>
    </div>
  );

  const skipOn = active === "skip";
  const CELL_IDLE_LOCAL: Record<"abroad" | "heavy", string> = {
    abroad: t.cellIdleBg("abroad"),
    heavy:  t.cellIdleBg("heavy"),
  };

  return (
    <div style={{ background: cardBg, borderRadius: 18, padding: "18px 16px", boxShadow: shadow }}>
      <div style={{ fontSize: t.titleSize, fontWeight: 800, color: v === "C" ? "#14532D" : t.titleColor, marginBottom: 4, letterSpacing: t.titleSize > 18 ? -0.5 : 0 }}>特殊情況回報</div>
      <div style={{ fontSize: 12, color: t.subtitleColor, marginBottom: 14 }}>告訴我們你的情況，讓預測更貼近你</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {(["abroad", "heavy"] as const).map((key) => {
          const on = active === key;
          const meta = GRID_OPTS[key];
          return (
            <button key={key} type="button" onClick={() => toggle(key)} style={{ padding: "15px 13px", borderRadius: 14, textAlign: "left", background: on ? meta.tint : CELL_IDLE_LOCAL[key], border: `1.5px solid ${on ? meta.color : t.cellIdleBorder}`, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s ease" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: on ? meta.color : C.ink }}>{meta.label}</span>
                {on && <RiCheckLine size={15} color={meta.color} />}
              </div>
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.45 }}>{meta.desc}</div>
            </button>
          );
        })}
      </div>

      {(active === "abroad" || active === "heavy") && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 8, letterSpacing: 0.2 }}>說明情況</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {SUB_OPTS[active].map((s) => <SubChip key={s.key} label={s.label} active={sub === s.key} onClick={() => setSub(sub === s.key ? null : s.key)} />)}
          </div>
        </div>
      )}

      <button type="button" onClick={() => toggle("skip")} style={{ marginTop: 10, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 14px", borderRadius: 14, background: skipOn ? "rgba(196,203,214,0.25)" : t.cellIdleBg("abroad" as any) === "#fff" ? "#F5F5F7" : "rgba(0,0,0,0.04)", border: `1.5px solid ${skipOn ? "#C4CBD6" : t.cellIdleBorder}`, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s ease" }}>
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>此次忽略，選擇原因</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>我知道了，這次不需要叫桶</div>
        </div>
        {skipOn ? <RiCheckLine size={17} color={C.muted} style={{ flexShrink: 0 }} /> : <RiArrowRightLine size={15} color={C.muted} style={{ flexShrink: 0 }} />}
      </button>

      {skipOn && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 8, letterSpacing: 0.2 }}>原因是？</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {SUB_OPTS.skip.map((s) => <SubChip key={s.key} label={s.label} active={sub === s.key} onClick={() => setSub(sub === s.key ? null : s.key)} />)}
          </div>
        </div>
      )}

      {active && (
        <button type="button" onClick={handleSubmit} style={{ marginTop: 14, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: C.blue, border: "none", borderRadius: 14, padding: "13px 16px", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 6px 18px -8px rgba(33,150,243,0.5)" }}>
          <div>
            <div style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>確認送出</div>
            <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 11, marginTop: 1 }}>{summaryText || situationLabel}</div>
          </div>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RiCheckLine size={16} color={C.blue} />
          </div>
        </button>
      )}
    </div>
  );
};

// ─── Variant-aware SurveySnapshotCard ─────────────────────────────────────────

const SurveySnapshotV = ({ record, onSurvey, v }: { record: GasSurveyRecord; onSurvey: () => void; v: StyleV }) => {
  const t = TOKENS[v];
  const usageText = record.usage_types?.length ? record.usage_types.map((x) => USAGE_LABELS[x] ?? x).join("、") : null;

  const rows: { label: string; value: string }[] = [
    { label: "自填週期", value: `${record.survey_days} 天` },
    { label: "鋼瓶規格", value: record.cylinder_kg ? `${record.cylinder_kg} kg` : "不確定" },
    ...(record.household_size ? [{ label: "家庭人數", value: HOUSEHOLD_LABELS[record.household_size] ?? record.household_size }] : []),
    ...(usageText ? [{ label: "主要用途", value: usageText }] : []),
  ];

  return (
    <div style={{ background: t.snapBg, borderRadius: 18, padding: "16px 16px 18px", boxShadow: v === "A" ? "0 2px 8px -5px rgba(31,41,55,0.10)" : t.cardShadow }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: v === "B" ? 19 : 15, fontWeight: 800, color: t.snapTitleColor, letterSpacing: v === "B" ? -0.5 : 0 }}>
            你的用氣習慣
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>問卷自填資料</div>
        </div>
        <button type="button" onClick={onSurvey} style={{ fontSize: 11.5, fontWeight: 600, color: t.snapUpdateColor, background: t.snapUpdateBg, border: "none", padding: "5px 12px", borderRadius: 999, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
          更新問卷
        </button>
      </div>

      {v === "B" ? (
        /* Variant B: 橫排清單 */
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {rows.map((row, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: i > 0 ? `1px solid ${C.border}` : "none" }}>
              <span style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>{row.label}</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>{row.value}</span>
            </div>
          ))}
        </div>
      ) : (
        /* Variant A / C: 2×2 格 */
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
          {rows.map((row, i) => (
            <div key={i} style={{ background: t.snapTileBg, borderRadius: 12, padding: "12px 13px", boxShadow: "0 1px 4px -2px rgba(31,41,55,0.08)" }}>
              <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 600, letterSpacing: 0.3, marginBottom: 6 }}>{row.label}</div>
              <div style={{ fontSize: row.value.length > 6 ? 13 : 17, fontWeight: 800, color: v === "C" ? t.snapTitleColor : C.ink, lineHeight: 1.2 }}>{row.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Style variant switcher ────────────────────────────────────────────────────

const VariantSwitcher = ({ current, onChange }: { current: StyleV; onChange: (v: StyleV) => void }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
    <span style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: 0.2, flexShrink: 0 }}>版面</span>
    <div style={{ display: "flex", gap: 6 }}>
      {STYLE_VARIANTS.map(({ key, label }) => {
        const on = current === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            style={{
              padding: "5px 13px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit",
              color: on ? "#fff" : C.muted,
              background: on ? C.ink : "#ECEEF2",
              border: "none",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}>
            {label}
          </button>
        );
      })}
    </div>
  </div>
);

// ─── Main export ──────────────────────────────────────────────────────────────

export interface FeedbackBlockProps {
  surveyRecord: GasSurveyRecord | null;
  contextId?: string | number | null;
  behaviorAlert?: BehaviorAlert | null;
  lastOrderDate?: string | null;
  avgDays?: number | null;
  notifyDate?: string | null;
  /** 有週期結果卡時不顯示 behavior_alert（互斥） */
  suppressBehaviorAlert?: boolean;
  onUpdateSurvey?: () => void;
}

export const FeedbackBlock = ({
  surveyRecord, contextId, behaviorAlert,
  lastOrderDate, avgDays, notifyDate,
  suppressBehaviorAlert = false,
  onUpdateSurvey,
}: FeedbackBlockProps) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
    {surveyRecord && (
      <SurveyDashboard
        record={surveyRecord}
        v="A"
        lastOrderDate={lastOrderDate}
        avgDays={avgDays}
        notifyDate={notifyDate}
        onUpdateSurvey={onUpdateSurvey}
      />
    )}
    {!suppressBehaviorAlert && behaviorAlert && (
      <BehaviorAlertCard alert={behaviorAlert} contextId={contextId} />
    )}
  </div>
);
